import React, {useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import {
  getMaxSectionWidth,
  getPanelTransform,
  getSnappedColumn,
} from '../../PanelBank/sectionLayout/resizeUtils';
import {WORKSPACE_MARGIN_X_TW} from '../../PanelBank/sectionLayout/utils';
import {useWorkspaceSectionLayoutConfigs} from '../../PanelBank/sectionLayout/WorkspaceSectionLayoutConfigs';

export type WorkspacePanelResizePreview = {
  index: number;
  height: number;
  width: number;
};

export const WorkspacePanelResizePreview = ({
  panelResizePreview,
}: {
  panelResizePreview: WorkspacePanelResizePreview | null;
}) => {
  const {columnsPerPage, panelWidth} = useWorkspaceSectionLayoutConfigs();

  // Memoize the max width calculation since it depends on config values
  const maxWidth = useMemo(
    () => getMaxSectionWidth({columnsPerPage, panelWidth}),
    [columnsPerPage, panelWidth]
  );

  const transform = useMemo(() => {
    if (panelResizePreview == null) {
      return undefined;
    }

    return getPanelTransform({
      index: panelResizePreview.index,
      columnsPerPage,
      panelWidth: panelResizePreview.width,
      panelHeight: panelResizePreview.height,
    });
    // Only recalculate if index changes (which shouldn't happen during resize)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [panelResizePreview?.index]);

  const previewSize = useMemo(() => {
    if (panelResizePreview == null) {
      return undefined;
    }

    // We want the x-axis to snap to increments of the current panel width
    // so panels can be spaced evenly on a row
    const {width: newColumnWidth} = getSnappedColumn({
      currPanelWidth: panelResizePreview.width,
      maxWidth,
    });

    return {
      width: newColumnWidth,
      height: panelResizePreview.height,
    };
  }, [maxWidth, panelResizePreview]);

  return (
    <div
      className={twMerge(
        'absolute left-0 top-0',
        'pointer-events-none select-none',
        'border-[1px] border-dashed border-wb-border-strong dark:border-moon-450',
        'bg-wb-surface-selected dark:bg-moon-400/[.5]',
        WORKSPACE_MARGIN_X_TW,
        panelResizePreview == null
          ? 'z-[0] opacity-0 hover:opacity-100'
          : 'z-[1] opacity-100' // Setting z-index to 1 to ensure the preview is on top of all the other panels
      )}
      // Tailwind doesn't support variables, so we need to use inline styles instead
      style={{
        ...previewSize,
        // We are intentionally using transforms to position the preview
        // to maintain performant resize interactions. Using position absolute
        // will cause unnecessary re-renders.
        transform,
      }}
    />
  );
};
